import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";

import { RequestError, makeRequest } from "../../utils/request";
import { Alert, AlertDescription, Badge, Button, DropdownField, EmptyState, Spinner, Text, View } from "../../components/base";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Icon } from "../../components/base/Icon";

type WorkloadStatus = "starting" | "ready" | "running" | "succeeded" | "failed" | "terminated";

type CoderunnerPod = {
  podName: string;
  status: WorkloadStatus;
  reason: string | null;
  restarts: number;
  desiredReplicas: number | null;
  activeSessionCount: number;
  lastActiveSessionAt: string | null;
  handlerStatus: string | null;
  startedAt: string | null;
  lastObservedAt: string;
  terminatedAt: string | null;
};

type CoderunnerBuild = {
  name: string;
  status: WorkloadStatus;
  reason: string | null;
  startedAt: string | null;
  lastObservedAt: string;
  terminatedAt: string | null;
};

type ReleaseOperationalStatus = "created" | "building" | "starting" | "running" | "degraded" | "stopping" | "standby" | "failed";

// Standby wears the brand color: deliberate and settled, distinct from
// transitions (warning), healthy (success), and trouble (error). Gray is
// reserved for `created` — the brief nothing-has-happened-yet moment.
const OPERATIONAL_BADGE_VARIANT: Record<ReleaseOperationalStatus, "success" | "warning" | "error" | "neutral" | "brand"> = {
  created: "neutral",
  building: "warning",
  starting: "warning",
  running: "success",
  degraded: "error",
  stopping: "warning",
  standby: "brand",
  failed: "error",
};

type CoderunnerRelease = {
  releaseId: string;
  cluster: string | null;
  operationalStatus: ReleaseOperationalStatus | null;
  desiredState: "running" | "standby" | null;
  releaseStatus: string | null;
  releaseCreatedAt: string | null;
  organizationId: string | null;
  organizationName: string | null;
  agentId: string | null;
  agentName: string | null;
  environmentId: string | null;
  environmentName: string | null;
  build: { phase: string; message?: string | null; image?: string | null; updatedAt: string } | null;
  pods: CoderunnerPod[];
  builds: CoderunnerBuild[];
};

type ControlPlane = { lastReportedAt: string; isStale: boolean } | null;
// One control plane per cluster, each with its own heartbeat. The singular
// `controlPlane` aggregate can mask a dead cluster behind a healthy one, so
// the header renders one badge per cluster from this list.
type ClusterControlPlane = { cluster: string; lastReportedAt: string; isStale: boolean };

const POLL_MS = 6000;

const STATUS_BADGE_VARIANT: Record<WorkloadStatus, "success" | "warning" | "error" | "default"> = {
  ready: "success",
  running: "success",
  succeeded: "default",
  starting: "warning",
  failed: "error",
  terminated: "default",
};

const relativeTime = (iso: string | null): string => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${Math.max(s, 0)}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const shortId = (id: string) => (id.length > 16 ? `${id.slice(0, 14)}…` : id);

const shortImage = (image?: string | null) => {
  if (!image) return null;
  // Registry prefixes are noise here; the repo:tag tail identifies the build.
  const tail = image.split("/").pop() || image;
  return tail.length > 40 ? `${tail.slice(0, 38)}…` : tail;
};

const LifecycleIconButton = ({ icon, label, disabled, onClick }: { icon: string; label: string; disabled?: boolean; onClick: () => void }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
  >
    <Icon
      name={icon}
      size={16}
    />
  </button>
);

export const AdminCoderunnerPodsPage = () => {
  const [releases, setReleases] = useState<CoderunnerRelease[]>([]);
  const [controlPlane, setControlPlane] = useState<ControlPlane>(null);
  const [controlPlanes, setControlPlanes] = useState<ClusterControlPlane[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lifecycleBusyId, setLifecycleBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingStop, setPendingStop] = useState<{ releaseId: string; reason: string } | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({ org: "", agent: "", environment: "", cluster: "", status: "", state: "" });

  const toggleExpanded = (releaseId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(releaseId)) next.delete(releaseId);
      else next.add(releaseId);
      return next;
    });
  };
  const [error, setError] = useState<string | null>(null);

  // Monotonic sequencing: interval ticks, manual refreshes, and post-action
  // reloads race; a slow older response must not overwrite a newer one.
  const loadSeq = useRef(0);
  const loadPods = useCallback(async (showSpinner = false) => {
    const seq = ++loadSeq.current;
    if (showSpinner) setIsLoading(true);
    try {
      const response = await makeRequest<{
        data: CoderunnerRelease[];
        controlPlane: ControlPlane;
        controlPlanes?: ClusterControlPlane[];
      }>("/v1/admin/coderunner/pods");
      if (seq !== loadSeq.current) return;
      setReleases(response.data);
      setControlPlane(response.controlPlane);
      setControlPlanes(response.controlPlanes ?? []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load coderunner pods");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Same org-facing endpoints the releases tab uses. Organization-Id is
  // OMITTED: the header narrows even super-admins to the selected org
  // server-side, which would 404 every cross-org action on this fleet page.
  //
  // Stop tries WITHOUT force first: the API's 409 (serving release — newest
  // for its env, or entrypoint-referenced) carries the authoritative reason,
  // which becomes the ConfirmDialog copy; confirming retries with force.
  const handleLifecycle = useCallback(
    async (releaseId: string, op: "stop" | "start" | "restart", force = false) => {
      setActionError(null);
      setLifecycleBusyId(releaseId);
      try {
        await makeRequest(`/v1/releases/${releaseId}/${op}${op === "stop" && force ? "?force=true" : ""}`, {
          method: "POST",
          omitOrganizationId: true,
        });
        await loadPods(false);
      } catch (e) {
        if (op === "stop" && !force && e instanceof RequestError && e.status === 409) {
          setPendingStop({ releaseId, reason: e.message });
        } else {
          setActionError(`Failed to ${op} release: ${e instanceof Error ? e.message : e}`);
        }
      } finally {
        setLifecycleBusyId(null);
      }
    },
    [loadPods],
  );

  useEffect(() => {
    loadPods(true);
    // The control plane pushes snapshots every ~15s; poll while the page is
    // open so observed state and the freshness banner stay live.
    const t = setInterval(() => loadPods(false), POLL_MS);
    return () => clearInterval(t);
  }, [loadPods]);

  // Client-side filters over the fully-loaded feed. Option lists derive from
  // the UNFILTERED data, and the currently-applied value stays selectable
  // even when the 6s poll's feed no longer contains it — otherwise the
  // dropdown renders its placeholder while the filter keeps hiding rows.
  const distinct = (values: Array<string | null>, keep: string) =>
    Array.from(new Set([...(keep ? [keep] : []), ...(values.filter(Boolean) as string[])])).sort();
  const orgOptions = distinct(
    releases.map((r) => r.organizationName),
    filters.org,
  );
  const agentOptions = distinct(
    releases.filter((r) => !filters.org || r.organizationName === filters.org).map((r) => r.agentName),
    filters.agent,
  );
  const environmentOptions = distinct(
    releases.map((r) => r.environmentName),
    filters.environment,
  );
  const clusterOptions = distinct(
    releases.map((r) => r.cluster),
    filters.cluster,
  );
  const stateOptions = distinct(
    releases.map((r) => r.operationalStatus),
    filters.state,
  );
  const statusOptions = distinct(
    releases.map((r) => r.releaseStatus),
    filters.status,
  );

  // The feed arrives grouped by lastObservedAt, which every ~15s snapshot
  // rewrites — without an explicit sort the rows shuffle on each poll.
  const releaseTime = (r: CoderunnerRelease) => {
    const t = r.releaseCreatedAt ? new Date(r.releaseCreatedAt).getTime() : NaN;
    return Number.isNaN(t) ? 0 : t;
  };
  const visibleReleases = releases
    .filter(
      (r) =>
        (!filters.org || r.organizationName === filters.org) &&
        (!filters.agent || r.agentName === filters.agent) &&
        (!filters.environment || r.environmentName === filters.environment) &&
        (!filters.cluster || r.cluster === filters.cluster) &&
        (!filters.status || r.releaseStatus === filters.status) &&
        (!filters.state || r.operationalStatus === filters.state),
    )
    .sort((a, b) => releaseTime(b) - releaseTime(a) || a.releaseId.localeCompare(b.releaseId));
  const hasActiveFilters = Object.values(filters).some(Boolean);

  const setFilter = (key: keyof typeof filters, value: string) => setFilters((prev) => ({ ...prev, [key]: value, ...(key === "org" ? { agent: "" } : {}) }));

  const allPods = releases.flatMap((r) => r.pods);
  const liveCount = allPods.filter((p) => !p.terminatedAt).length;

  return (
    <View className="h-full w-full flex flex-col bg-gray-50 dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <Text className="text-xl font-semibold">Coderunner</Text>
          <Text className="text-sm text-gray-500 dark:text-gray-400">
            {releases.length} {releases.length === 1 ? "release" : "releases"} · {liveCount} live {liveCount === 1 ? "pod" : "pods"}
          </Text>
          {controlPlanes.length > 0
            ? controlPlanes.map((plane) =>
                plane.isStale ? (
                  <Badge
                    key={plane.cluster}
                    variant="error"
                  >
                    {plane.cluster} stale — last reported {relativeTime(plane.lastReportedAt)}
                  </Badge>
                ) : (
                  <Text
                    key={plane.cluster}
                    className="text-xs text-gray-400 dark:text-gray-500"
                  >
                    {plane.cluster} reported {relativeTime(plane.lastReportedAt)}
                  </Text>
                ),
              )
            : controlPlane &&
              (controlPlane.isStale ? (
                <Badge variant="error">monitoring stale — last reported {relativeTime(controlPlane.lastReportedAt)}</Badge>
              ) : (
                <Text className="text-xs text-gray-400 dark:text-gray-500">reported {relativeTime(controlPlane.lastReportedAt)}</Text>
              ))}
        </div>
        <Button
          variant="ghost"
          type="button"
          onClick={() => loadPods(true)}
        >
          <Icon
            name="refresh"
            size={18}
            className="text-brand"
          />
        </Button>
      </div>

      {/* Filters — client-side over the loaded feed */}
      {(releases.length > 0 || hasActiveFilters) && (
        <div className="flex flex-wrap items-center gap-2 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
          {(
            [
              ["org", "All organizations", orgOptions],
              ["agent", "All agents", agentOptions],
              ["environment", "All environments", environmentOptions],
              ["cluster", "All clusters", clusterOptions],
              ["status", "All statuses", statusOptions],
              ["state", "All states", stateOptions],
            ] as Array<[keyof typeof filters, string, string[]]>
          ).map(([key, placeholder, options]) => (
            <View
              key={key}
              className="w-auto min-w-[160px]"
            >
              <DropdownField
                name={key}
                placeholder={placeholder}
                value={filters[key]}
                onChange={(value: string) => setFilter(key, value)}
                options={[{ label: placeholder, value: "" }, ...options.map((option) => ({ label: option, value: option }))]}
              />
            </View>
          ))}
        </div>
      )}

      {actionError && (
        <div className="px-6 pt-3">
          <Alert variant="destructive">
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        </div>
      )}

      <ConfirmDialog
        isOpen={pendingStop !== null}
        onClose={() => setPendingStop(null)}
        onConfirm={() => {
          const pending = pendingStop;
          setPendingStop(null);
          if (pending) void handleLifecycle(pending.releaseId, "stop", true);
        }}
        title="Stop serving release?"
        message={pendingStop?.reason ?? ""}
        confirmLabel="Stop anyway"
        variant="danger"
      />

      {/* Release groups */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-2">
        {isLoading ? (
          <View className="flex items-center justify-center py-20">
            <Spinner className="!text-brand" />
          </View>
        ) : error ? (
          <View className="flex items-center justify-center py-20">
            <Text className="text-error">{error}</Text>
          </View>
        ) : releases.length === 0 ? (
          <EmptyState
            icon="bot"
            title="No coderunner workloads observed"
            description="The coderunner control plane reports dedicated release pods and builds here as soon as it observes them — nothing is running right now."
          />
        ) : visibleReleases.length === 0 ? (
          <EmptyState
            icon="bot"
            title="No releases match the filters"
            description="Clear or change the filters above."
          />
        ) : (
          visibleReleases.map((release) => {
            const isExpanded = expandedIds.has(release.releaseId);
            const liveCount = release.pods.filter((p) => !p.terminatedAt).length;
            return (
              <div
                key={release.releaseId}
                className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              >
                {/* Compact row — click to expand. A role=button div, not a
                    <button>: the lifecycle actions inside are real buttons and
                    button-in-button is invalid HTML screen readers flatten. */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleExpanded(release.releaseId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleExpanded(release.releaseId);
                    }
                  }}
                  aria-expanded={isExpanded}
                  className={clsx(
                    "w-full flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-left cursor-pointer",
                    isExpanded && "border-b border-gray-100 dark:border-gray-700",
                  )}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon
                      name={isExpanded ? "chevron-down" : "chevron-right"}
                      size={16}
                      className="text-gray-400 shrink-0"
                    />
                    <Text className="text-sm font-medium truncate">
                      {release.organizationName ?? "Unknown org"} · {release.agentName ?? "Unknown agent"}
                    </Text>
                    {release.environmentName && <Badge variant="info">{release.environmentName}</Badge>}
                    {release.cluster && <Badge variant="default">{release.cluster}</Badge>}
                    {release.operationalStatus && <Badge variant={OPERATIONAL_BADGE_VARIANT[release.operationalStatus]}>{release.operationalStatus}</Badge>}
                    <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                      {liveCount} {liveCount === 1 ? "pod" : "pods"}
                      {release.releaseCreatedAt && ` · ${relativeTime(release.releaseCreatedAt)}`}
                    </span>
                  </div>
                  {release.operationalStatus && (
                    <div
                      className="flex items-center gap-0.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {release.desiredState === "standby" ? (
                        <LifecycleIconButton
                          icon="play"
                          label="Start release"
                          disabled={lifecycleBusyId === release.releaseId}
                          onClick={() => handleLifecycle(release.releaseId, "start")}
                        />
                      ) : (
                        <>
                          <LifecycleIconButton
                            icon="refresh"
                            label="Restart release (rolling bounce, same image)"
                            disabled={lifecycleBusyId === release.releaseId || release.operationalStatus === "building"}
                            onClick={() => handleLifecycle(release.releaseId, "restart")}
                          />
                          <LifecycleIconButton
                            icon="stop"
                            label="Stop release (standby — image kept, start is seconds)"
                            disabled={lifecycleBusyId === release.releaseId || release.operationalStatus === "building"}
                            onClick={() => handleLifecycle(release.releaseId, "stop")}
                          />
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Detail — release id, build, pods, builds */}
                {isExpanded && (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
                      <span
                        className="font-mono text-[11px] text-gray-400 dark:text-gray-500 truncate"
                        title={release.releaseId}
                      >
                        {release.releaseId}
                      </span>
                      {release.build && (
                        <span
                          className="text-xs text-gray-500 dark:text-gray-400"
                          title={release.build.message ?? undefined}
                        >
                          Build: {release.build.phase}
                          {release.build.image && (
                            <span className="ml-2 font-mono text-[11px] text-gray-400 dark:text-gray-500">{shortImage(release.build.image)}</span>
                          )}
                        </span>
                      )}
                    </div>
                    {release.pods.map((pod) => (
                      <div
                        key={pod.podName}
                        className={clsx("flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5", pod.status === "failed" && "bg-error/5 dark:bg-error/10")}
                      >
                        <span
                          className="font-mono text-xs text-gray-700 dark:text-gray-300 truncate flex-1 min-w-[180px]"
                          title={pod.podName}
                        >
                          {pod.podName}
                        </span>
                        <Badge variant={STATUS_BADGE_VARIANT[pod.status]}>{pod.status}</Badge>
                        {pod.reason && pod.status !== "terminated" && <Badge variant="warning">{pod.reason}</Badge>}
                        {pod.restarts > 0 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {pod.restarts} {pod.restarts === 1 ? "restart" : "restarts"}
                          </span>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400 w-20">
                          {pod.activeSessionCount} {pod.activeSessionCount === 1 ? "session" : "sessions"}
                        </span>
                        <span
                          className="text-xs text-gray-500 dark:text-gray-400 w-28"
                          title={pod.lastObservedAt}
                        >
                          seen {relativeTime(pod.lastObservedAt)}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 w-32">
                          {pod.terminatedAt ? `stopped ${relativeTime(pod.terminatedAt)}` : `up since ${relativeTime(pod.startedAt)}`}
                        </span>
                      </div>
                    ))}

                    {/* Builds (kaniko Job pods; visible while in flight and briefly after) */}
                    {release.builds.map((build) => (
                      <div
                        key={build.name}
                        className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 bg-gray-50/50 dark:bg-gray-900/30"
                      >
                        <span
                          className="font-mono text-xs text-gray-500 dark:text-gray-400 truncate flex-1 min-w-[180px]"
                          title={build.name}
                        >
                          {build.name}
                        </span>
                        <Badge variant="info">build</Badge>
                        <Badge variant={STATUS_BADGE_VARIANT[build.status]}>{build.status}</Badge>
                        {build.reason && <Badge variant="warning">{build.reason}</Badge>}
                        <span
                          className="text-xs text-gray-500 dark:text-gray-400 w-28"
                          title={build.lastObservedAt}
                        >
                          seen {relativeTime(build.lastObservedAt)}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 w-32">started {relativeTime(build.startedAt)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </View>
  );
};
